import { LRUCache } from 'shared/utils/lruCache'
import {
    BestPracticeSyncAction,
    SyncActionResult,
} from '../../smartProjectCopy/types'

/** Properties that affect sync status — cached alongside the result for validation on read */
export interface SyncCacheContext {
    isHidden: boolean
}

interface CachedSyncEntry {
    result: SyncActionResult
    context: SyncCacheContext
}

// Module-level cache that survives component remounts (virtual tree scroll).
// Uses LRU eviction when max size is reached.
// 200 entries covers large projects (typically 50–150 BP items)
const MAX_CACHE_SIZE = 200

const syncResultCache = new LRUCache<string, CachedSyncEntry>(MAX_CACHE_SIZE)

export const SYNCED_RESULT: SyncActionResult = {
    action: BestPracticeSyncAction.None,
    currentVersionId: '',
    isUserAddedContentBestPractice: false,
    latestVersionId: '',
    message: 'Synced',
}

/**
 * Read a cached sync result. Returns undefined (cache miss) if:
 * - No entry exists for this key, OR
 * - The stored context no longer matches current state (e.g. isHidden changed)
 */
export const getCacheEntry = (
    key: string,
    currentContext: SyncCacheContext
): SyncActionResult | undefined => {
    const entry = syncResultCache.peek(key)

    if (!entry) return undefined
    if (entry.context.isHidden !== currentContext.isHidden) return undefined

    return entry.result
}

export const setCacheEntry = (
    key: string,
    result: SyncActionResult,
    context: SyncCacheContext
) => syncResultCache.set(key, { result, context })

/** Invalidate a single cache entry — used when content is edited */
export const invalidateCacheEntry = (key: string) => syncResultCache.delete(key)

/** Clear the sync result cache — for test cleanup only */
export const clearSyncResultCache = () => syncResultCache.clear()
